import React from 'react'

import { Display, Visible } from '@gousto-internal/citrus-react'
import { DeliveryInstructions } from 'DeliveryInstructions/DeliveryInstructions'
import { DeliveryTips } from 'DeliveryTips/DeliveryTips'
import classNames from 'classnames'
import { useSelector } from 'react-redux'

import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import { Calendar } from 'routes/Signup/Components/Calendar/Calendar'
import { DeliveryDay } from 'routes/Signup/Components/Calendar/models/DeliveryDay'
import { StickyCTA } from 'routes/Signup/Components/StickyCTA/StickyCTA'
import { SocialBelongingBanner } from 'routes/Signup/SocialBelongingBanner'
import { trackSocialBelongingBannerAppearance } from 'routes/Signup/signupActions'
import { getAmountOfCustomers, getDistrict } from 'routes/Signup/signupSelectors'
import {
  getDeliveryDetailsInstructions,
  getIsValidDeliveryDetailsInstructionsCustom,
} from 'selectors/deliveryDetails'

import { Button } from '../../Button'

import signupCss from '../../Signup.css'
import css from './DeliveryStep.css'

type DeliveryStepWithCalendarProps = {
  deliveryDays: DeliveryDay[]
  tempDate: string
  onTempDateChange: (date: string) => void
  dateLabel: string
  onShowRecipe: () => void
}

export const DeliveryStepWithCalendar = ({
  deliveryDays,
  tempDate,
  onTempDateChange,
  dateLabel,
  onShowRecipe,
}: DeliveryStepWithCalendarProps) => {
  const deliveryInstructions = useSelector(getDeliveryDetailsInstructions)
  const isValidDeliveryDetailsInstructionsCustom = useSelector(
    getIsValidDeliveryDetailsInstructionsCustom,
  )
  const district = useSelector(getDistrict)
  const amountOfCustomers = useSelector(getAmountOfCustomers)
  const showSocialBelongingBanner = district && amountOfCustomers > 50
  // setting isWizardFloatingCTAEnabled to false as we don't want to remove it just yet and this way we don't need to actually evaluate it
  const isWizardFloatingCTAEnabled = false

  let ctaDisabled
  if (!deliveryInstructions) {
    ctaDisabled = true
  } else {
    ctaDisabled = !isValidDeliveryDetailsInstructionsCustom
  }

  return (
    <>
      {showSocialBelongingBanner && (
        <SocialBelongingBanner
          amountOfCustomers={amountOfCustomers}
          district={district}
          trackBannerAppearance={trackSocialBelongingBannerAppearance}
        />
      )}
      <div className={css.calendarContainer}>
        <div data-testing="signupDeliveryDay" data-testid="signupDeliveryDay">
          <Calendar
            deliveryDays={deliveryDays}
            selectedDay={tempDate}
            onDayChange={onTempDateChange}
          />
        </div>
        <>
          <DeliveryInstructions />
          <DeliveryTips />
        </>
      </div>
      <div className={signupCss.footer}>
        <div className={classNames(signupCss.inputContainer, css.confirmButton)}>
          <Visible
            display={[isWizardFloatingCTAEnabled ? Display.None : Display.Block, Display.Block]}
          >
            <Button
              data-testing="signupDeliveryCTA"
              data-testid="signupDeliveryCTA"
              width="full"
              onClick={onShowRecipe}
              disabled={ctaDisabled}
            >
              {`Select ${dateLabel}`}
            </Button>
          </Visible>
          {isWizardFloatingCTAEnabled && (
            <Visible display={[Display.Block, Display.None]}>
              <StickyCTA onClick={onShowRecipe} data-testid="signupDeliveryCTAMobile">
                {`Select ${dateLabel}`}
              </StickyCTA>
            </Visible>
          )}
        </div>
      </div>
      <RibbonTriggerContainer name="wizard-delivery-options" />
    </>
  )
}
